import { useState, useEffect } from "react";
import { Boxes, Plus, Trash2, Loader2, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const brl = (n) => Number(n || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 });

export default function ResellerPackages() {
  const [floor, setFloor] = useState(0);
  const [packages, setPackages] = useState([]);
  const [form, setForm] = useState({ name: "", credits: "", price: "", active: true });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/reseller/packages")
    .then(({ data }) => { setFloor(data.floor_per_credit); setPackages(data.packages || []); })
    .catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const perCredit = Number(form.credits) > 0 ? Number(form.price) / Number(form.credits) : 0;
  const minPrice = floor * (Number(form.credits) || 0);
  const belowFloor = perCredit > 0 && perCredit < floor;

  const create = async () => {
    if (!form.name.trim() || !form.credits || !form.price) return toast.error("Preencha nome, créditos e preço.");
    setSaving(true);
    try {
      await api.post("/reseller/packages", { name: form.name.trim(), credits: Number(form.credits), price: Number(form.price), active: form.active });
      toast.success("Pacote criado!"); setForm({ name: "", credits: "", price: "", active: true }); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const remove = async (id) => { try { await api.delete(`/reseller/packages/${id}`); toast.success("Pacote removido"); load(); } catch (e) { toast.error(apiError(e)); } };
  const toggle = async (p) => { try { await api.patch(`/reseller/packages/${p.id}`, { name: p.name, credits: p.credits, price: p.price, active: !p.active, order: p.order || 0 }); load(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div className="max-w-3xl space-y-6" data-testid="reseller-packages">
      <div className="flex items-center gap-2"><Boxes className="h-6 w-6 text-primary" /><h2 className="font-heading text-xl font-bold tracking-tight sm:text-2xl">Pacotes de créditos</h2></div>
      <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <span>Você vende créditos para os revendedores da sua árvore. O preço por crédito não pode ser menor que o piso do admin: <b className="text-foreground">R$ {brl(floor)}/crédito</b>. Os créditos saem da sua carteira; o dinheiro cai no seu gateway (ou no seu saldo, se o comprador pagar com saldo).</span>
      </div>

      <div className="grid gap-4 rounded-xl border border-border bg-card p-5 sm:grid-cols-4">
        <div className="space-y-2 sm:col-span-2"><Label>Nome do pacote</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Pacote 100" data-testid="pkg-name-input" /></div>
        <div className="space-y-2"><Label>Créditos</Label><Input type="number" value={form.credits} onChange={(e) => setForm({ ...form, credits: e.target.value })} data-testid="pkg-credits-input" /></div>
        <div className="space-y-2"><Label>Preço (R$)</Label><Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="pkg-price-input" /></div>
        <div className="sm:col-span-4 flex flex-wrap items-center justify-between gap-2">
          <p className={`text-xs ${belowFloor ? "text-destructive" : "text-muted-foreground"}`} data-testid="pkg-percredit-hint">
            {Number(form.credits) > 0 && form.price
              ? <>R$ {brl(perCredit)}/crédito{belowFloor ? ` · abaixo do piso (mínimo R$ ${brl(minPrice)})` : ""}</>
              : `Mínimo: R$ ${brl(floor)}/crédito`}
          </p>
          <Button className="gap-1.5" onClick={create} disabled={saving} data-testid="pkg-create-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Criar pacote</Button>
        </div>
      </div>

      <div className="space-y-2">
        {packages.length === 0 && <p className="rounded-lg border border-dashed border-border py-12 text-center text-muted-foreground">Nenhum pacote ainda. Crie o primeiro acima.</p>}
        {packages.map((p) => (
          <div key={p.id} className="flex items-center gap-3 rounded-lg border border-border bg-card p-4" data-testid={`pkg-row-${p.id}`}>
            <div className="min-w-0 flex-1">
              <p className="font-medium">{p.name}</p>
              <p className="text-xs text-muted-foreground">{p.credits} créditos · R$ {brl(p.price)} · R$ {brl(p.price / p.credits)}/crédito</p>
            </div>
            <label className="flex items-center gap-2 text-xs text-muted-foreground"><Switch checked={p.active} onCheckedChange={() => toggle(p)} data-testid={`pkg-active-${p.id}`} /> Ativo</label>
            <Button variant="ghost" size="icon" className="text-destructive hover:bg-destructive/10" onClick={() => remove(p.id)} data-testid={`pkg-delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}
